import _ from 'lodash';
import template from './dialPanel.html';

export default {
  name: 'dialPanel',
  template,
  controller: DialPanelController,
  bindings: {
    panelId: '<'
  }
};

/* @ngInject */
function DialPanelController(
  $scope,
  $interval,
  growl,
  EquipmentInfoService,
  DashboardService,
  ModalService,
  PumpsService,
  GatesService,
  AccessControlService,
  GenetecService,
  $q
) {

  /// ////////////////
  // Local Variables//
  /// ////////////////
  const vm = this;
  const timeToWaitInterval = {};
  const ONE_SECOND = 1000;
  const EQUIPMENT = {
    gates: {
      service: GatesService,
      closedState: 'holdNormal',
      openAction: 'setGateOpen'
    },
    pumps: {
      service: PumpsService
    },
    accessControls: {
      service: AccessControlService,
      closedState: 'locked',
      openAction: 'accessDoor'
    },
    genetec: {
      service: GenetecService,
      closedState: 'doorLock'
    }
  };

  /// /////////////////
  // Local Functions //
  /// /////////////////

  function showLoadingSpinner() {
    vm.loadingSpinners.stopDialPanel = false;
  }

  function hideLoadingSpinner() {
    vm.loadingSpinners.stopDialPanel = true;
  }

  function getActiveEqSchedule() {
    return _.get(vm, `dialModel.${vm.activeEquipment}.state.schedule`);
  }

  function canEnableEqSchedule(equipmentType = getEquipmentType()) {
    return _.has(EQUIPMENT, [equipmentType, 'service', 'enableSchedule']);
  }

  function isActiveEqScheduleEnabled() {
    return _.get(vm, `dialModel.${vm.activeEquipment}.state.scheduleEnabled`);
  }

  function getCurrentDialOption() {
    return _.get(vm, `dialModel.${vm.activeEquipment}.state.currentOption`);
  }

  function getOptions(equipmentType) {
    return EQUIPMENT[equipmentType].service.getOptions();
  }

  function getState(equipmentId, equipmentType) {
    const eqService = _.get(EQUIPMENT, [equipmentType, 'service']);
    if (!eqService) {
      return $q.reject('equipmentType not recognized');
    }
    return eqService.getStatus(vm.dialModel[equipmentId].equipment);
  }

  function equipmentIsClosed() {
    return getCurrentDialOption() === _.get(EQUIPMENT, [getEquipmentType(), 'closedState']);
  }

  /// /////////////////
  // Public Variables//
  /// /////////////////
  vm.dialModel = {};
  vm.dialHeaderModel = {};
  vm.loadingSpinners = {
    stopDialPanel: false // true means stop spinner
  };
  vm.editPanel = {
    needsToBeConfigured: false
  };
  vm.activeEquipment = null;

  /// /////////////////
  // Public Functions//
  /// /////////////////
  vm.$onInit = init;
  vm.$postLink = postLink;
  vm.setActiveDial = setActiveDial;
  vm.openEquipment = openEquipment;
  vm.scheduleModal = scheduleModal;
  vm.performActionMethod = performActionMethod;
  vm.getEquipmentType = getEquipmentType;
  vm.equipmentCanBeOpened = equipmentCanBeOpened;
  vm.shouldEnableDialOpenAction = shouldEnableDialOpenAction;
  vm.shouldEnableDialScheduleAction = shouldEnableDialScheduleAction;


  /// //////////////////////
  // Function Declarations//
  /// //////////////////////
  function init() {
    vm.currentDashboard = DashboardService.getCurrentDashboard();
    vm.panel = vm.currentDashboard.panels[vm.panelId];
    if (_.isEmpty(vm.panel.equipment)) {
      vm.editPanel.needsToBeConfigured = true;
    } else {
      vm.editPanel.needsToBeConfigured = false;
      EquipmentInfoService.getFullEquipment(vm.panel.equipment)
        .then((success) => {
          if (_.isEmpty(success)) {
            return $q.reject('Unable to access equipment');
          }
          vm.dialHeaderModel = _.keyBy(success, 'id');
          const initEq = (equipment) => {
            vm.dialModel[equipment.id] = {
              equipment,
              options: getOptions(equipment.equipmentType),
              state: {
                currentOption: null
              },
              unreachable: false
            };
            return getState(equipment.id, getEquipmentType(equipment.id))
              .then((state) => {
                vm.dialModel[equipment.id].state = state;
              })
              .catch(() => {
                vm.dialModel[equipment.id].unreachable = true;
              })
              .finally(() => {
                if (!vm.activeEquipment) {
                  setActiveDial(equipment);
                }
              });
          };
          return $q.all(success.map(initEq));
        })
        .catch(() => {
          vm.dialModel.ajaxError = true;
        });
    }
  }

  function setActiveDial(equipment) {
    vm.activeEquipment = equipment.id;
    updateTimeToWait(equipment.id);
  }

  function openEquipment(equipmentId) {
    const equipmentType = getEquipmentType();
    if (!equipmentCanBeOpened(equipmentType)) {
      return console.error('Invalid equipment type');
    }

    let growlMessage = growl.info('Opening...', { disableCloseButton: true });
    const { openAction } = EQUIPMENT[equipmentType];
    return EQUIPMENT[equipmentType].service
      .performAction(vm.dialModel[equipmentId].equipment, openAction)
      .then(() => {
        growlMessage.destroy();
        growlMessage = growl.success('Successfully Opened.');
      })
      .catch((error) => {
        growlMessage.destroy();
        growl.warning(error, {
          title: 'Error Opening Equipment: '
        });
      });
  }

  function postLink() {
    _.forEach(vm.panel.equipment, (equipment) => {
      $scope.$on(`event:${equipment}`, (aeve, eve) => handleSocketMessage(aeve, eve));
    });
  }

  function handleSocketMessage(__, { equipment, data }) {
    const foundModel = vm.dialModel[equipment.id];
    const eqService = _.get(EQUIPMENT, [equipment.equipmentType, 'service']);
    if (foundModel && eqService) {
      foundModel.state = eqService.processCurrentOption(data);
      if (equipment.id === vm.activeEquipment) {
        updateTimeToWait(equipment.id);
      }
    }
  }

  /**
   * Check if timeToWait state has a value.
   * Activates the wait message and decrements the timeToWait value.
   *
   * @param {string} equipment ID
   */
  function updateTimeToWait(equipment) {
    const eqState = vm.dialModel[equipment].state;
    if (eqState && eqState.timeToWait > 0) {
      if (timeToWaitInterval[equipment]) {
        $interval.cancel(timeToWaitInterval[equipment]);
      }
      showLoadingSpinner();
      timeToWaitInterval[equipment] = $interval(() => {
        eqState.timeToWait--;
        if (eqState.timeToWait <= 0) {
          if (equipment === vm.activeEquipment) {
            hideLoadingSpinner();
          }
          $interval.cancel(timeToWaitInterval[equipment]);
        }
      }, ONE_SECOND);
    } else {
      hideLoadingSpinner();
    }
  }

  function scheduleModal() {
    const eq = vm.dialModel[vm.activeEquipment].equipment;
    const service = EQUIPMENT[getEquipmentType()].service;
    const scheduleOptions = {
      title: `Schedule ${getEquipmentType()}`,
      eq,
      scheduleSettings: getActiveEqSchedule(),
      scheduleEnabled: isActiveEqScheduleEnabled(),
      canEnableSchedule: canEnableEqSchedule()
    };
    return ModalService
      .openScheduler(scheduleOptions)
      .then(({ scheduleSettings, scheduleEnabled }) => {
        let growlMessage = growl.info('Saving schedule...', { disableCloseButton: true });
        return service
          .setSchedule(vm.activeEquipment, scheduleSettings)
          .then(() => {
            vm.dialModel[vm.activeEquipment].state.schedule = scheduleSettings;
            if (canEnableEqSchedule()) {
              return service.enableSchedule(vm.activeEquipment, scheduleEnabled)
                .then(() => {
                  vm.dialModel[vm.activeEquipment].state.scheduleEnabled = scheduleEnabled;
                });
            }
            return true;
          })
          .then(() => {
            growlMessage.destroy();
            growlMessage = growl.success('Schedule saved successfully.');
          })
          .catch((error) => {
            growlMessage.destroy();
            growlMessage = growl.error(error, { title: 'Error saving schedule' });
          });
      });
  }

  function shouldEnableDialOpenAction() {
    return equipmentCanBeOpened() && equipmentIsClosed();
  }

  function shouldEnableDialScheduleAction(equipmentType = getEquipmentType()) {
    return _.has(EQUIPMENT, [equipmentType, 'service', 'setSchedule']);
  }

  /**
   * Detects currently selected equipment and performs given action on appropriate service.
   *
   * @param {string} action
   * @returns {Promise}
   */
  function performActionMethod(action) {
    showLoadingSpinner();
    return EQUIPMENT[getEquipmentType()]
      .service
      .performAction(vm.dialModel[vm.activeEquipment].equipment, action)
      .finally(hideLoadingSpinner);
  }

  /**
   * @param {string} [equipmentType] Optional equipment type string
   * @returns {boolean} Openable?
   */
  function equipmentCanBeOpened(equipmentType = getEquipmentType()) {
    return _.has(EQUIPMENT, [equipmentType, 'openAction']);
  }

  function getEquipmentType(equipmentId = vm.activeEquipment) {
    return _.get(vm, `dialModel.${equipmentId}.equipment.equipmentType`);
  }

}
